/**
 * TypeScript interfaces for WebSocket RPC/control messages.
 *
 * AI SDK v6 event types are provided by @ai-sdk/react and the 'ai' package.
 * These types are for RPC and control messages that extend the WebSocket protocol.
 */

// JSON-RPC 2.0 error structure
export interface RpcError {
  code: number;
  message: string;
  data?: unknown;
}

// RPC request message (bidirectional)
export interface RpcRequest {
  type: 'rpc-request';
  jsonrpc: '2.0';
  id: string;
  method: string;
  params?: Record<string, unknown>;
}

// RPC notification message (fire-and-forget, no response expected)
// Per JSON-RPC 2.0 spec: notifications have no id and MUST NOT receive a reply
export interface RpcNotification {
  type: 'rpc-notification';
  jsonrpc: '2.0';
  method: string;
  params?: Record<string, unknown>;
}

// RPC response message (bidirectional)
export interface RpcResponse {
  type: 'rpc-response';
  jsonrpc: '2.0';
  id: string;
  result?: unknown;
  error?: RpcError;
}

// Control message: stop chat streaming
export interface ChatStop {
  type: 'chat-stop';
  chatId: string;
}

// Control message: heartbeat for connection health
export interface Heartbeat {
  type: 'heartbeat';
  timestamp: number;
}

// Control message: error notification
export interface ErrorMessage {
  type: 'error';
  code: string;
  message: string;
}

// Chat request sent to backend
export interface ChatRequest {
  session_id: string;
  user_id: string;
  message: string;
}

// Control message: tools changed notification (browser → server)
export interface ToolsChanged {
  type: 'tools-changed';
  tools: BrowserToolDefinition[];
}

// Spec 048/089: capability handshake + per-tool lifecycle frames
export interface CapabilitiesMessage {
  type: 'capabilities';
  supports: string[];
  protocolVersion?: string;
  clientVersion?: string;
}

export interface ToolRegisterMessage {
  type: 'tool-register';
  tool: BrowserToolDefinition;
}

export interface ToolUnregisterMessage {
  type: 'tool-unregister';
  name: string;
}

export interface ToolUpdateMessage {
  type: 'tool-update';
  tool: BrowserToolDefinition;
}

export interface ToolsDeltaMessage {
  type: 'tools-delta';
  added?: BrowserToolDefinition[];
  removed?: string[];
  updated?: BrowserToolDefinition[];
}

// Union type for all RPC/control messages
export type RpcMessage = RpcRequest | RpcNotification | RpcResponse;
export type ControlMessage =
  | ChatStop
  | Heartbeat
  | ErrorMessage
  | ToolsChanged
  | CapabilitiesMessage
  | ToolRegisterMessage
  | ToolUnregisterMessage
  | ToolUpdateMessage
  | ToolsDeltaMessage;
export type WebSocketControlMessage = RpcMessage | ControlMessage;

/** Server-known progressive-disclosure groups (spec 089 data-model §2). */
export type BrowserToolGroup =
  | 'browser'
  | 'browser_navigation'
  | 'browser_data'
  | 'browser_interaction'
  | 'browser_render';

// Browser tool definition for discovery
export interface BrowserToolDefinition {
  name: string;
  description: string;
  parameters: Record<string, unknown>;
  /** Tool category; the server maps it to a disclosure group as fallback. */
  category?: string;
  /** Progressive-disclosure group used by the agent toolkit (spec 089). */
  disclosureGroup?: BrowserToolGroup;
  /** Agent slugs allowed to discover this tool. Omit for all agents. */
  visibleToAgents?: string[];
  requiresPermission?: string[];
  /** If true, this tool is fire-and-forget (no response expected) */
  fireAndForget?: boolean;
  /**
   * MCP-standard annotations — clients use these for trust decisions
   * (auto-approve read-only, require confirmation for destructive, …).
   */
  annotations?: {
    readOnlyHint?: boolean;
    destructiveHint?: boolean;
    idempotentHint?: boolean;
    openWorldHint?: boolean;
  };
  /** JSON Schema for the tool's return value, when declared. */
  outputSchema?: Record<string, unknown>;
}

// Tool definitions response
export interface ToolDefinitionsResponse {
  tools: BrowserToolDefinition[];
}

// Standard JSON-RPC 2.0 error codes
export const RpcErrorCodes = {
  PARSE_ERROR: -32700,
  INVALID_REQUEST: -32600,
  METHOD_NOT_FOUND: -32601,
  INVALID_PARAMS: -32602,
  INTERNAL_ERROR: -32603,
  CONNECTION_CLOSED: -32000,
  REQUEST_TIMEOUT: -32001,
  PERMISSION_DENIED: -32002,
  SESSION_NOT_FOUND: -32003,
  UNKNOWN_TOOL: -32004,
} as const;
